import React, { useMemo, memo } from 'react';
import { Plus, Check, InfoCircle, Phone } from '@saleshandy/icons';
import { Spinner } from 'react-bootstrap';
import { OverlayTooltip } from '../../../../../../../../shared/design-system/components/overlay';
import Alert from '../../../../../../../../shared/design-system/ui/alert';
import { Images } from '../../../../../../../../shared/app-constants';
import SearchNumbers from './components/search-numbers/search-numbers-container';
import SuggestedPhoneNumbersTable from './components/suggested-phone-numbers-table';
import SearchingPhoneNumbers from './components/searching-phone-numbers';
import NoPhoneNumberFound from './components/no-phone-number-found/no-phone-number-found';
import { getIsRequestPending } from '../../../../../../../../shared/utils/get-request-status';

import { SelectNumbersStepProps } from './select-numbers-step-container';
import Mobile from '../../../../../../../../shared/svg/mobile';

const SelectNumbersStep: React.FC<SelectNumbersStepProps> = memo(
  ({
    phoneNumbersInCart,
    onToggleNumberSelection,
    isSearching,
    formattedPhoneNumbersData,
    addPhoneNumbersToCartRequest,
    removePhoneNumberFromCartRequest,
    freeNumbersQuota,
  }) => {
    const isLoading = isSearching;
    const cartCount = useMemo(() => phoneNumbersInCart?.length || 0, [
      phoneNumbersInCart,
    ]);

    const data = useMemo(
      () =>
        formattedPhoneNumbersData.map((number) => {
          const normalizedPhoneNumber = number.phoneNumber.replace(/\s/g, '');
          const isAddLoading = getIsRequestPending(
            addPhoneNumbersToCartRequest[normalizedPhoneNumber]?.status,
          );
          const isRemoveLoading = getIsRequestPending(
            removePhoneNumberFromCartRequest[normalizedPhoneNumber]?.status,
          );
          const isActionRequestPending = isAddLoading || isRemoveLoading;
          
          return {
            ...number,
            isSelected: phoneNumbersInCart.some(
              (item) => item.phoneNumber.replace(/\s/g, '') === normalizedPhoneNumber,
            ),
            isActionRequestPending,
          };
        }),
      [formattedPhoneNumbersData, phoneNumbersInCart, addPhoneNumbersToCartRequest, removePhoneNumberFromCartRequest],
    );

    const columns = useMemo(
      () => [
        {
          key: 'phoneNumber',
          header: 'Phone Number',
          component: (row: any) => {
            const { phoneNumber, isLoading: rowIsLoading } = row;
            return rowIsLoading ? (
              (phoneNumber as JSX.Element)
            ) : (
              <span>{phoneNumber}</span>
            );
          },
        },
        {
          key: 'region',
          header: 'Region',
          component: (row: any) => {
            const { formattedRegion, isLoading: rowIsLoading } = row;
            return rowIsLoading ? (
              (formattedRegion as JSX.Element)
            ) : (
              <span>{formattedRegion}</span>
            );
          },
        },
        {
          key: 'numberType',
          header: (
            <>
              <p style={{ margin: 0, marginRight: '4px' }}>Number Type</p>
              <OverlayTooltip text="Number type refers to the category of phone number (e.g., Local, Mobile)">
                <InfoCircle
                  color="#6B7280"
                  width={14}
                  height={14}
                  style={{ cursor: 'pointer' }}
                />
              </OverlayTooltip>
            </>
          ),
          component: (row: any) => {
            const { numberType, isLoading: rowIsLoading } = row;
            return rowIsLoading ? (
              (numberType as JSX.Element)
            ) : (
              <div className="number-type-badge">
                <div className="number-type-badge-content">
                  {numberType === 'Mobile' ? (
                    <Mobile width={14} height={14} />
                  ) : (
                    <Phone width={14} height={14} />
                  )}
                  <span>{numberType}</span>
                </div>
              </div>
            );
          },
        },
        {
          key: 'addressRequirement',
          header: (
            <>
              <p style={{ margin: 0, marginRight: '4px' }}>
                Address Requirement
              </p>
              <OverlayTooltip text="Some local authorities requires you to provide the address to comply with the regulations">
                <InfoCircle
                  color="#6B7280"
                  width={14}
                  height={14}
                  style={{ cursor: 'pointer' }}
                />
              </OverlayTooltip>
            </>
          ),
          component: (row: any) => {
            const { addressRequirement, isLoading: rowIsLoading } = row;
            return rowIsLoading ? (
              (addressRequirement as JSX.Element)
            ) : (
              <span>{addressRequirement}</span>
            );
          },
        },
        {
          key: 'pricePerQuarter',
          header: 'Price per quarter',
          component: (row: any) => {
            const { pricePerQuarter, isLoading: rowIsLoading } = row;
            return rowIsLoading ? (
              (pricePerQuarter as JSX.Element)
            ) : (
              <span>{pricePerQuarter}</span>
            );
          },
        },
        {
          key: 'action',
          header: '',
          component: (row: any) => {
            const {
              phoneNumber,
              isSelected = false,
              isLoading: rowIsLoading,
              action,
              isActionRequestPending,
            } = row;

            if (rowIsLoading) {
              return action as JSX.Element;
            }
            if(isActionRequestPending) {
              return (
                <Spinner className="spinner--blue bw-1" animation="border" size="sm" />
              );
            }

            if (isSelected) {
              return (
                <OverlayTooltip
                  text="Remove from Cart"
                  key={`${phoneNumber}-remove`}
                >
                  <Check
                    className="remove-from-cart-icon"
                    color="green"
                    width={20}
                    height={20}
                    onClick={() => onToggleNumberSelection(phoneNumber)}
                  />
                </OverlayTooltip>
              );
            }

            return (
              <OverlayTooltip
                text={cartCount > 9 ? 'Purchase limit reached' : 'Add to Cart'}
                key={`${phoneNumber}-add`}
              >
                <Plus
                  className={`add-to-cart-icon ${
                    cartCount > 9 ? 'disabled' : ''
                  }`}
                  width={20}
                  height={20}
                  onClick={
                    cartCount > 9
                      ? undefined
                      : () => onToggleNumberSelection(phoneNumber)
                  }
                />
              </OverlayTooltip>
            );
          },
        },
      ],
      [onToggleNumberSelection, cartCount],
    );

    const tableContent = () => {
      if (isLoading) {
        return (
          <SearchingPhoneNumbers
            columns={columns}
            tableTitle={<p>Available Numbers</p>}
            cartCount={cartCount}
          />
        );
      }
      if (!isLoading && data.length === 0) {
        return <NoPhoneNumberFound />;
      }
      return (
        <SuggestedPhoneNumbersTable
          columns={columns}
          data={data}
          tableTitle={<p>Available Numbers</p>}
          rowClassName={(row) => (row.isSelected ? 'row-selected' : '')}
          tableHeightClassname="purchase-phone-numbers-height"
          cartCount={cartCount}
        />
      );
    };

    return (
      <div className="select-numbers-step-container">
        <div className="select-numbers-step-search-container">
          <SearchNumbers />
        </div>
        {freeNumbersQuota > 0 && (
          <div className="free-number-eligibility-banner">
            <Alert
              className="free-number-alert"
              variant="info"
              description={
                <div className="free-number-alert-content">
                  <img
                    src={Images.AlertCircleBlue}
                    alt="Info"
                    width={16}
                    height={16}
                  />
                  <p>
                    You are eligible for {freeNumbersQuota} free US phone{' '}
                    {freeNumbersQuota === 1 ? 'number' : 'numbers'} based on
                    your current plan. The discount will be applied to your
                    total.
                  </p>
                </div>
              }
            />
          </div>
        )}
        <div className="select-numbers-step-numbers-container">
          {tableContent()}
        </div>
      </div>
    );
  },
);

SelectNumbersStep.displayName = 'SelectNumbersStep';

export default SelectNumbersStep;
